import { atom } from 'jotai';
import { shuffle } from 'lodash-es';

import type { EvidenceSBSCandidate } from '@heidi/evidence/contracts';
import {
  evidenceSBSAbortControllersAtom,
  evidenceSBSCandidatesAtom,
  evidenceSBSChatIdAtom,
  evidenceSBSDisplayedTimeAtom,
  evidenceSBSExperimentModeAtom,
  evidenceSBSMessagesAtom,
  evidenceSBSSessionIdAtom,
  evidenceSBSStartedChatIdAtom,
  evidenceSBSStreamAAtom,
  evidenceSBSStreamBAtom,
  evidenceSBSUserQueryAtom,
  makeInitialStreamState,
} from '@heidi/evidence/data';
import { logger } from '@heidi/runtime/logger';
import { postEvidenceSBSSetup, type EvidenceSBSSetupRequest } from '../api';

export const evidenceSBSSetupAction = atom(
  null,
  async (get, set, reqBody: EvidenceSBSSetupRequest) => {
    set(evidenceSBSDisplayedTimeAtom, null);
    try {
      const response = await postEvidenceSBSSetup(reqBody);
      const shuffled = shuffle(response.data.candidates);
      set(
        evidenceSBSCandidatesAtom,
        shuffled.map((c): EvidenceSBSCandidate => ({ ...c, confirmed: false }))
      );

      if (response.data.candidates.length > 1) {
        set(evidenceSBSExperimentModeAtom, response.data.generation_mode);
      }

      return get(evidenceSBSCandidatesAtom);
    } catch (error) {
      logger.error('[EVIDENCE_SBS] setup failed:', error);
      return undefined;
    }
  }
);

export const confirmEvidenceSBSCandidateAction = atom(
  null,
  (get, set, candidateId: string) => {
    set(
      evidenceSBSCandidatesAtom,
      get(evidenceSBSCandidatesAtom).map((c) =>
        c.candidate_id === candidateId ? { ...c, confirmed: true } : c
      )
    );
  }
);

export const afterConfirmEvidenceSBSAction = atom(null, (_get, set) => {
  set(resetEvidenceSBSAction);
});

export const skipEvidenceSBSAction = atom(null, (_get, set) => {
  set(resetEvidenceSBSAction);
});

export const resetEvidenceSBSAction = atom(null, (get, set) => {
  // Tear down in-flight SBS streams. This is the ONLY place we abort —
  // dismiss / skip / confirm / starting a new round all funnel through here.
  // The panel unmounting (e.g. switching to another tab and back) deliberately
  // does NOT abort, so the streams keep filling in while it's away.
  get(evidenceSBSAbortControllersAtom).forEach((ac) => ac.abort());
  set(evidenceSBSAbortControllersAtom, []);
  set(evidenceSBSStartedChatIdAtom, null);
  set(evidenceSBSStreamAAtom, makeInitialStreamState());
  set(evidenceSBSStreamBAtom, makeInitialStreamState());

  set(evidenceSBSExperimentModeAtom, '');
  set(evidenceSBSCandidatesAtom, []);
  set(evidenceSBSDisplayedTimeAtom, null);
  set(evidenceSBSUserQueryAtom, '');
  set(evidenceSBSMessagesAtom, []);
  set(evidenceSBSChatIdAtom, null);
  set(evidenceSBSSessionIdAtom, null);
});
